import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react'

/* Port of customSnacBar (custom_code/actions/custom_snac_bar.dart):
 *
 *   customSnacBar(context, message, backgroundColor?, borderRadius?, hide)
 *   -> SnackBar(shape: radius(borderRadius ?? 30),
 *               backgroundColor: backgroundColor ?? primary,
 *               behavior: floating, margin: EdgeInsets.all(20),
 *               content: Text(message))
 *
 * Every live call passes hideCurrentSnackBar: true -> single-slot replace.
 * Duration is the Flutter SnackBar default: 4000 ms.
 * Content text renders white (M2 SnackBar default content colour on a dark
 * background), default 16px. */

const TOAST_MS = 4000

interface Toast {
  key: number
  message: string
  background: string
  radius: number
}

interface ToastApi {
  /** customSnacBar equivalent. Defaults mirror the Dart signature. */
  show: (message: string, background?: string, radius?: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) throw new Error('useToast must be used inside <ToastProvider>')
  return api
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const counter = useRef(0)

  const show = useCallback(
    (message: string, background?: string, radius?: number) => {
      // hideCurrentSnackBar(): replace whatever is showing.
      if (timer.current) clearTimeout(timer.current)
      counter.current += 1
      setToast({
        key: counter.current,
        message,
        background: background ?? 'var(--primary)',
        radius: radius ?? 30,
      })
      timer.current = setTimeout(() => setToast(null), TOAST_MS)
    },
    [],
  )

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {toast && (
        <div
          key={toast.key}
          role="status"
          style={{
            position: 'fixed',
            left: 20,
            right: 20,
            bottom: 20,
            zIndex: 1000,
            background: toast.background,
            borderRadius: toast.radius,
            // Floating SnackBar default content padding & elevation 6.
            padding: '14px 16px',
            boxShadow:
              '0 3px 5px -1px rgba(0,0,0,.2), 0 6px 10px 0 rgba(0,0,0,.14), 0 1px 18px 0 rgba(0,0,0,.12)',
            color: '#ffffff',
            fontFamily: 'var(--font-inter)',
            fontSize: 16,
            lineHeight: 1.25,
          }}
        >
          {toast.message}
        </div>
      )}
    </ToastContext.Provider>
  )
}
